import { type FC, type FormEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog as HeadlessDialog } from '@headlessui/react';
import { useLocation, useNavigate, useRevalidator, useRouteLoaderData } from '@remix-run/react';
import cn from 'classnames';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { COMMUNITY_ROUTES } from '~/constants/community';
import useIsMobile from '~/hooks/useIsMobile';
import type { CommunityProfileField, CommunitySpace } from '~/interfaces/community';
import type { loader as communityLayoutLoader } from '~/routes/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { POPOVER_PANEL_CLASSES } from './ui/_styles/popover';
import { BottomSheetDialog } from './ui/BottomSheetDialog';
import { Dialog } from './ui/Dialog';
import { ModalCloseButton } from './ui/ModalCloseButton';
import { PillButton } from './ui/PillButton';
import { PillLink } from './ui/PillLink';

import { useInvalidateCommunityFeeds } from '../_hooks/useInvalidateCommunityFeeds';
import { useOpenChannelUpgradeModal } from '../_hooks/useOpenChannelUpgradeModal';
import useProfileQuestionAnswers from '../_hooks/useProfileQuestionAnswers';
import { useSpaceAccess } from '../_hooks/useSpaceAccess';
import { isUnanswered } from '../_utils/onboardingProfileFields';
import { ProfileFieldInput } from './ProfileFieldInput';

interface AnswerQuestionModalProps {
  spaceId: string | null;
}

interface QuestionDialogProps {
  space: CommunitySpace;
  field: CommunityProfileField;
}

const QuestionDialog: FC<QuestionDialogProps> = ({ space, field }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { handleCloseModal } = useGlobalModals();
  const { revalidate } = useRevalidator();
  const { canAccessSpace } = useSpaceAccess();
  const invalidateCommunityFeeds = useInvalidateCommunityFeeds();
  const openChannelUpgradeModal = useOpenChannelUpgradeModal();
  const layoutData = useRouteLoaderData<typeof communityLayoutLoader>('routes/community');
  const fields = useMemo(() => [field], [field]);
  const { answers, fieldErrors, handleAnswerChange, submitAnswers, isPending } = useProfileQuestionAnswers({
    fields,
    errorDisplay: 'inline',
  });
  const spaceAccess = layoutData?.spaceAccess;
  // Saving reloads the layout, and its new access map says whether the answer opened the space.
  const [submittedAccess, setSubmittedAccess] = useState<typeof spaceAccess>();
  const isSaved = submittedAccess != null && spaceAccess !== submittedAccess;
  const isReloading = submittedAccess != null && !isSaved;
  const isOpen = canAccessSpace(space);
  const hasOpenedUpgrade = useRef(false);
  // A matching answer to a channel that also needs a paid tier leaves it locked, with the upgrade path.
  const needsTier = isSaved && spaceAccess?.[space.id]?.reason === 'tier';
  const channelName = space.name;
  const spacePath = COMMUNITY_ROUTES.spaces(space.slug);
  const value = answers[field.id] ?? null;

  const close = () => {
    if (isPending || isReloading) return;
    // A hidden channel's page says it isn't available, so the member leaves it for the community home.
    if (isSaved && !isOpen && pathname.startsWith(spacePath)) {
      navigate(COMMUNITY_ROUTES.root);
      return;
    }
    handleCloseModal({ preventScrollReset: true });
  };

  // Opening the upgrade modal swaps it in through the URL, so it runs once after the reload shows the channel needs a tier.
  useEffect(() => {
    if (!needsTier || hasOpenedUpgrade.current) return;
    hasOpenedUpgrade.current = true;
    openChannelUpgradeModal(space.id, NectarPageBlock.CommunityChannelUpgradeModal);
  }, [needsTier, openChannelUpgradeModal, space.id]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    submitAnswers(fields, () => {
      setSubmittedAccess(spaceAccess);
      invalidateCommunityFeeds();
      // A different answer hides the channel and its posts, so a post page would turn into "no longer available".
      // Navigating keeps the modal's search params; revalidating during it reloads the layout's access too.
      if (pathname.startsWith(COMMUNITY_ROUTES.post(''))) {
        navigate({ pathname: COMMUNITY_ROUTES.root, search }, { preventScrollReset: true });
      }
      revalidate();
    });
  };

  const titleClassName = 'pr-8 text-xl font-semibold leading-6 tracking-[-0.4px] text-black';
  const isSaving = isPending || isReloading || needsTier;
  const outcome = isOpen
    ? {
        title: t('community.answer_question.matched_title', { channelName }),
        description: t('community.answer_question.matched_description'),
        action: (
          <PillLink to={spacePath} variant="primary" size="md" fullWidth className="mt-6">
            {t('community.answer_question.matched_cta', { channelName })}
          </PillLink>
        ),
      }
    : {
        title: t('community.answer_question.mismatched_title'),
        description: t('community.answer_question.mismatched_description', { channelName }),
        action: (
          <PillButton variant="primary" size="md" fullWidth className="mt-6" onClick={close}>
            {t('community.answer_question.done')}
          </PillButton>
        ),
      };

  const content = () => {
    if (isSaved && !needsTier) {
      return (
        <>
          <HeadlessDialog.Title className={titleClassName}>{outcome.title}</HeadlessDialog.Title>
          <p className="mt-2 text-[15px] leading-5 text-black/50">{outcome.description}</p>
          {outcome.action}
        </>
      );
    }

    return (
      <form onSubmit={handleSubmit} noValidate>
        <p className="text-[13px] font-medium text-black/50">
          {t('community.answer_question.eyebrow', { channelName })}
        </p>
        <HeadlessDialog.Title className={cn('mt-1', titleClassName)}>
          {field.asked_as || field.display}
        </HeadlessDialog.Title>
        <div className="mt-4">
          <ProfileFieldInput
            field={field}
            value={value}
            error={fieldErrors[field.id]?.[0]}
            disabled={isSaving}
            hideLabel
            onChange={(nextValue) => handleAnswerChange(field.id, nextValue)}
          />
        </div>
        <PillButton
          type="submit"
          variant="primary"
          size="md"
          fullWidth
          className="mt-6"
          disabled={isSaving || isUnanswered(field, value)}
        >
          {isSaving ? t('community.shared.saving') : t('community.answer_question.save')}
        </PillButton>
      </form>
    );
  };

  const body = (
    <>
      <div className="absolute right-4 top-4">
        <ModalCloseButton onClose={close} label={t('shared.close')} className="size-8" />
      </div>
      {content()}
    </>
  );

  if (isMobile) {
    return (
      <BottomSheetDialog isOpen onClose={close} panelClassName="relative px-5 pb-8 pt-6">
        {body}
      </BottomSheetDialog>
    );
  }

  return (
    <Dialog isOpen onClose={close} className={cn(POPOVER_PANEL_CLASSES, 'relative w-full max-w-sm border-0 p-6')}>
      {body}
    </Dialog>
  );
};

export const AnswerQuestionModal: FC<AnswerQuestionModalProps> = ({ spaceId }) => {
  const layoutData = useRouteLoaderData<typeof communityLayoutLoader>('routes/community');
  const { profileFieldIdFor } = useSpaceAccess();
  // A different answer removes the space and its question from the layout, so the modal keeps what it opened with.
  const [question] = useState(() => {
    const space = layoutData?.spaces?.find((layoutSpace) => layoutSpace.id === spaceId);
    const profileFieldId = space && profileFieldIdFor(space);
    const field = layoutData?.profileFields.find((profileField) => profileField.id === profileFieldId);
    return space && field ? { space, field } : null;
  });

  if (!question) return null;

  return <QuestionDialog space={question.space} field={question.field} />;
};
